<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import parsePhoneNumber from 'libphonenumber-js';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from '../../../../featureFlags';
import WithLabel from 'v3/components/Form/WithLabel.vue';
import NextInput from 'next/input/Input.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import PhoneNumberInput from 'dashboard/components-next/phonenumberinput/PhoneNumberInput.vue';
import AccountDelete from './components/AccountDelete.vue';
import AudioTranscription from './components/AudioTranscription.vue';
import SectionLayout from './components/SectionLayout.vue';
const props = defineProps({
  embedded: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t, locale: globalLocale } = useI18n();
const { uiSettings } = useUISettings();
const { accountId } = useAccount();

const name = ref('');
const domain = ref('');
const supportEmail = ref('');
const fullPhone = ref('');
const accountCountryIso = ref('');
const features = ref({});
const phoneInputRef = ref(null);

const rules = {
  name: { required },
};
const formState = { name };
const v$ = useVuelidate(rules, formState);

const getAccount = computed(() => store.getters['accounts/getAccount']);
const uiFlags = computed(() => store.getters['accounts/getUIFlags']);
const isFeatureEnabledonAccount = (id, flag) =>
  store.getters['accounts/isFeatureEnabledonAccount'](id, flag);
const isOnChatwootCloud = computed(
  () => store.getters['globalConfig/isOnChatwootCloud']
);

const showAudioTranscriptionConfig = computed(() =>
  isFeatureEnabledonAccount(accountId.value, FEATURE_FLAGS.CAPTAIN)
);

const isUpdating = computed(() => uiFlags.value.isUpdating);

const showAccountLoading = computed(
  () => uiFlags.value.isFetchingItem && !props.embedded
);
const showAccountForm = computed(
  () => !uiFlags.value.isFetchingItem || props.embedded
);
const showAccountDeleteSection = computed(
  () =>
    (props.embedded || !uiFlags.value.isFetchingItem) && isOnChatwootCloud.value
);

const featureInboundEmailEnabled = computed(
  () => !!features.value?.inbound_emails
);
const featureCustomReplyEmailEnabled = computed(
  () => featureInboundEmailEnabled.value && !!features.value?.custom_reply_email
);

function buildFullPhoneFromAccount(acc) {
  const code = acc.phone_country_code || '';
  const digits = (acc.phone_number || '').replace(/\D/g, '');
  if (!code && !digits) return '';
  const joined = `${code}${digits}`;
  const parsed = parsePhoneNumber(joined);
  return parsed?.format('E.164') || joined;
}

function initializeAccount() {
  try {
    const acc = getAccount.value(accountId.value);
    const { name: n, domain: d, support_email, features: f } = acc;

    const effectiveLocale = uiSettings.value?.locale || 'en';
    globalLocale.value = effectiveLocale;
    name.value = n;
    domain.value = d || '';
    supportEmail.value = support_email;
    accountCountryIso.value = acc.country_iso || acc.country?.iso_alpha2 || '';
    fullPhone.value = buildFullPhoneFromAccount(acc);
    features.value = f;
  } catch {
    // Ignore error
  }
}

function phoneUpdatePayload() {
  const parts = phoneInputRef.value?.getPhoneParts?.() ?? {
    phone_country_code: null,
    phone_number: null,
  };
  const empty = !parts.phone_country_code && !parts.phone_number;
  if (empty) {
    return {
      country_id: null,
      phone_country_code: null,
      phone_number: null,
    };
  }
  return {
    phone_country_code: parts.phone_country_code,
    phone_number: parts.phone_number,
  };
}

async function submitAccount() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    useAlert(t('GENERAL_SETTINGS.FORM.ERROR'));
    return;
  }
  try {
    await store.dispatch('accounts/update', {
      name: name.value,
      domain: domain.value,
      support_email: supportEmail.value,
      ...phoneUpdatePayload(),
    });
    globalLocale.value = uiSettings.value?.locale || 'en';
    initializeAccount();
    useAlert(t('GENERAL_SETTINGS.UPDATE.SUCCESS'));
  } catch {
    useAlert(t('GENERAL_SETTINGS.UPDATE.ERROR'));
  }
}

watch(
  () => getAccount.value(accountId.value),
  account => {
    if (props.embedded && account) initializeAccount();
  },
  { immediate: true }
);

onMounted(() => {
  if (!props.embedded) {
    store.dispatch('accounts/get');
  }
  initializeAccount();
});
</script>

<template>
  <div class="flex w-full min-w-0 flex-col">
    <BaseSettingsHeader
      v-if="!props.embedded"
      :title="$t('GENERAL_SETTINGS.ACCOUNT_INFO_PAGE.TITLE')"
      :description="$t('GENERAL_SETTINGS.ACCOUNT_INFO_PAGE.DESCRIPTION')"
    />
    <div class="w-full min-w-0" :class="props.embedded ? '' : 'px-8 md:px-10'">
      <div
        class="flex flex-col"
        :class="props.embedded ? 'w-full' : 'max-w-[28rem]'"
      >
        <SectionLayout
          :title="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.TITLE')"
          :description="$t('GENERAL_SETTINGS.FORM.GENERAL_SECTION.NOTE')"
          class="!pt-0"
        >
          <form
            v-if="showAccountForm"
            class="grid gap-4"
            @submit.prevent="submitAccount"
          >
            <WithLabel
              name="account-name"
              :has-error="v$.name.$error"
              :label="$t('GENERAL_SETTINGS.FORM.NAME.LABEL')"
              :error-message="$t('GENERAL_SETTINGS.FORM.NAME.ERROR')"
            >
              <NextInput
                v-model="name"
                type="text"
                class="w-full"
                :placeholder="$t('GENERAL_SETTINGS.FORM.NAME.PLACEHOLDER')"
                @blur="v$.name.$touch"
              />
            </WithLabel>
            <WithLabel
              name="organization-phone"
              :label="$t('GENERAL_SETTINGS.FORM.ORGANIZATION_PHONE.LABEL')"
            >
              <PhoneNumberInput
                :key="String(accountId)"
                ref="phoneInputRef"
                v-model="fullPhone"
                class="w-full min-w-0"
                :country-hint="accountCountryIso"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.ORGANIZATION_PHONE.PLACEHOLDER')
                "
              />
            </WithLabel>
            <WithLabel
              name="custom-domain"
              :label="$t('GENERAL_SETTINGS.FORM.DOMAIN.LABEL')"
            >
              <NextInput
                v-model="domain"
                type="text"
                class="w-full"
                :placeholder="$t('GENERAL_SETTINGS.FORM.DOMAIN.PLACEHOLDER')"
              />
            </WithLabel>
            <WithLabel
              v-if="featureCustomReplyEmailEnabled"
              name="support-email"
              :label="$t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.LABEL')"
            >
              <NextInput
                v-model="supportEmail"
                type="text"
                class="w-full"
                :placeholder="
                  $t('GENERAL_SETTINGS.FORM.SUPPORT_EMAIL.PLACEHOLDER')
                "
              />
            </WithLabel>
            <div>
              <NextButton blue :is-loading="isUpdating" type="submit">
                {{ $t('GENERAL_SETTINGS.SUBMIT') }}
              </NextButton>
            </div>
          </form>
        </SectionLayout>

        <woot-loading-state v-if="showAccountLoading" />
      </div>
      <AudioTranscription v-if="showAudioTranscriptionConfig" />
      <div v-if="showAccountDeleteSection">
        <AccountDelete />
      </div>
    </div>
  </div>
</template>
